<script setup lang="ts">
import IconArrowDown from "@/assets/icons/IconArrowDown.vue";
import {computed} from "vue";

interface StatBlockProps {
  title: string;
  value: number | null | undefined;
  percent: number | null | undefined;
  isAppg: boolean;
  isMain?: boolean;
}

const props = defineProps<StatBlockProps>();

const isPositivePercent = computed(() => props.percent != null && props.percent > 0);

const percentClass = computed(() => {
  if (props.isAppg) {
    return "stat-block__percent--appg";
  }

  if (isPositivePercent.value) {
    return props.isMain ? "stat-block__percent--positive-main" : "stat-block__percent--positive";
  } else {
    return props.isMain ? "stat-block__percent--negative-main" : "stat-block__percent--negative";
  }
});

const getFormattedValue = computed(() => {
  if (props.value == null) return "н/д";
  return props.value.toLocaleString();
});
</script>

<template>
  <div
    class="stat-block"
    :class="{'stat-block--main': isMain}"
  >
    <div class="stat-block__header">
      <span class="stat-block__value">
        {{ getFormattedValue }}
      </span>
      <span
        v-if="props.value != null"
        class="stat-block__percent"
        :class="[percentClass]"
      >
        <IconArrowDown
          v-if="!isAppg"
          class="stat-block__arrow"
          :class="{'stat-block__arrow--positive': isPositivePercent}"
        />
        <span
          v-else
          class="stat-block__percent--appg"
        >АППГ</span>
        {{ percent?.toFixed(1) ?? '0.0' }}%
      </span>
    </div>
    <span class="stat-block__title">
      {{ props.title }}
    </span>
  </div>
</template>

<style scoped lang="scss">
.stat-block {
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  background-color: var(--background-bg-white);
  box-shadow: 1px 2px 10px 0 var(--gray-gray50);
  padding: 16px 14px 16px 16px;
  border-radius: 8px;

  min-height: 150px;

  &--main {
    background-color: var(--oracul-skyblue-skyblue10);
  }

  &__header {
    display: flex;
    gap: 8px;
    align-items: flex-start;
  }

  &__arrow {

    &--positive {
      transform: rotateX(180deg);
    }
  }

  &__value {
    font-weight: 700;
    font-size: 30px;
    line-height: 30px;
  }

  &__percent {
    display: flex;
    align-items: center;
    gap: 2px;
    border-radius: 4px;
    padding: 1px 2px 1px 2px;
    font-weight: 500;
    font-size: 16px;
    line-height: 100%;
    height: 21px;

    &--positive:not(&--positive-main) {
      color: var(--oracul-green-green50);
      background-color: var(--oracul-green-green10);
    }

    &--positive-main {
      color: var(--oracul-white-white);
      background-color: var(--oracul-green-green40);
    }

    &--negative:not(&--negative-main) {
      color: var(--oracul-red-red40);
      background-color: var(--oracul-red-red10);
    }

    &--negative-main {
      color: var(--oracul-white-white);
      background-color: var(--oracul-red-red30);
    }

    &--appg {
      font-size: 16px;
      font-weight: 500;
      color: var(--oracul-skyblue-skyblue100);
      background-color: var(--oracul-skyblue-skyblue10);
    }
  }

  &__title {
    font-size: 15px;
    line-height: 100%;
    font-weight: 400;
  }
}

:deep(.ps__rail-y) {
  background-color: #FFFFFF !important;
  opacity: 1 !important;
  width: 7px !important;
  right: 0;
}

:deep(.ps__thumb-y) {
  background-color: #EBEBEB !important;
  border-radius: 30px !important;
  width: 7px !important;
}
</style>